import { motion } from "framer-motion";

/**
 * Decorative abstract scientific motifs.
 * - Pure SVG, pointer-events:none.
 * - Slow, organic motion driven by Framer Motion.
 * - Reused across sections to give the landing page a more abstract, editorial-art feel.
 *
 * Props:
 *   variant: "rings" | "arcs" | "constellation" | "wave" | "sigil"
 *   tone: "navy" | "teal" | "gold" | "ink"
 *   className: positioning utility classes
 */
export default function AbstractShapes({
  variant = "rings",
  tone = "navy",
  className = "",
}) {
  const stroke = {
    navy: "rgba(11,22,44,0.55)",
    teal: "rgba(0,122,138,0.7)",
    gold: "rgba(184,150,110,0.8)",
    ink: "rgba(11,22,44,0.18)",
    white: "rgba(255,255,255,0.5)",
  }[tone] || tone;

  const fill = {
    navy: "rgba(11,22,44,0.04)",
    teal: "rgba(0,122,138,0.06)",
    gold: "rgba(184,150,110,0.08)",
    ink: "rgba(11,22,44,0.025)",
    white: "rgba(255,255,255,0.05)",
  }[tone] || "transparent";

  return (
    <div className={`pointer-events-none absolute ${className}`} aria-hidden="true">
      <svg
        viewBox="0 0 400 400"
        className="w-full h-full"
        fill="none"
        preserveAspectRatio="xMidYMid meet"
      >
        {variant === "rings" && (
          <g>
            <motion.circle
              cx="200" cy="200" r="190"
              stroke={stroke} strokeWidth="0.5" fill="none"
              animate={{ rotate: 360 }}
              transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: "200px 200px" }}
              strokeDasharray="2 6"
            />
            <motion.circle
              cx="200" cy="200" r="148"
              stroke={stroke} strokeWidth="0.7" fill="none"
              animate={{ rotate: -360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: "200px 200px" }}
            />
            <circle cx="200" cy="200" r="108" stroke={stroke} strokeWidth="0.4" />
            <circle cx="200" cy="200" r="70" fill={fill} stroke={stroke} strokeWidth="0.3" />
            <circle cx="200" cy="200" r="3" fill={stroke} />
            {/* Orbiting node */}
            <motion.g
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: "200px 200px" }}
            >
              <circle cx="200" cy="52" r="3.5" fill={stroke} />
              <circle cx="200" cy="52" r="9" stroke={stroke} strokeWidth="0.6" fill="none" />
            </motion.g>
            <motion.g
              animate={{ rotate: -360 }}
              transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: "200px 200px" }}
            >
              <circle cx="92" cy="200" r="2.5" fill={stroke} />
            </motion.g>
          </g>
        )}

        {variant === "arcs" && (
          <g>
            <motion.path
              d="M 20 380 Q 200 -40 380 380"
              stroke={stroke} strokeWidth="0.8" fill="none"
              strokeDasharray="320"
              initial={{ strokeDashoffset: 320 }}
              whileInView={{ strokeDashoffset: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 2.4, ease: "easeOut" }}
            />
            <motion.path
              d="M 40 380 Q 200 60 360 380"
              stroke={stroke} strokeWidth="0.5" fill="none" opacity="0.55"
              strokeDasharray="280"
              initial={{ strokeDashoffset: 280 }}
              whileInView={{ strokeDashoffset: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 2.4, delay: 0.2, ease: "easeOut" }}
            />
            <motion.path
              d="M 70 380 Q 200 140 330 380"
              stroke={stroke} strokeWidth="0.4" fill="none" opacity="0.35"
              strokeDasharray="240"
              initial={{ strokeDashoffset: 240 }}
              whileInView={{ strokeDashoffset: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 2.4, delay: 0.4, ease: "easeOut" }}
            />
            <circle cx="200" cy="60" r="3" fill={stroke} />
          </g>
        )}

        {variant === "constellation" && (
          <g stroke={stroke} fill={stroke} strokeWidth="0.5">
            <line x1="40" y1="80" x2="120" y2="140" />
            <line x1="120" y1="140" x2="200" y2="100" />
            <line x1="200" y1="100" x2="290" y2="180" />
            <line x1="290" y1="180" x2="360" y2="120" />
            <line x1="120" y1="140" x2="180" y2="230" />
            <line x1="180" y1="230" x2="290" y2="180" />
            <line x1="180" y1="230" x2="240" y2="320" />
            <line x1="240" y1="320" x2="340" y2="280" />
            {[
              [40,80],[120,140],[200,100],[290,180],[360,120],
              [180,230],[240,320],[340,280],[90,310],[300,60],
            ].map(([x,y],i) => (
              <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 3 : 2} />
            ))}
          </g>
        )}

        {variant === "wave" && (
          <g>
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.path
                key={i}
                d={`M 0 ${180 + i * 18} Q 100 ${160 + i * 18} 200 ${180 + i * 18} T 400 ${180 + i * 18}`}
                stroke={stroke} strokeWidth="0.5" fill="none" opacity={0.8 - i * 0.15}
                animate={{ x: [0, -20, 0] }}
                transition={{ duration: 8 + i, repeat: Infinity, ease: "easeInOut" }}
              />
            ))}
          </g>
        )}

        {variant === "sigil" && (
          <g stroke={stroke} fill="none" strokeWidth="0.7">
            {/* Cross + hexagon - editorial mark */}
            <polygon points="200,40 340,120 340,280 200,360 60,280 60,120" />
            <polygon points="200,90 295,145 295,255 200,310 105,255 105,145" opacity="0.55" />
            <line x1="60" y1="200" x2="340" y2="200" opacity="0.4" />
            <line x1="200" y1="40" x2="200" y2="360" opacity="0.4" />
            <circle cx="200" cy="200" r="40" fill={fill} />
            <circle cx="200" cy="200" r="6" fill={stroke} />
          </g>
        )}
      </svg>
    </div>
  );
}
